
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, isPrerender } from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const loadUser = useCallback(async () => {
    if (isPrerender) {
      setLoading(false);
      return;
    }
    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.me();
      setUser(res.data.user);
      fetchNotifications();
    } catch {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const fetchNotifications = async () => {
    if (isPrerender) return;
    try {
      const res = await authAPI.getNotifications();
      setNotifications(res.data.notifications || []);
    } catch {}
  };

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('token', res.data.token);
    }
    const loggedInUser = res.data.user ?? null;
    setUser(loggedInUser);
    toast.success(`Welcome back, ${loggedInUser?.name ?? 'there'}!`);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await authAPI.register({ name, email, password });
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('token', res.data.token);
    }
    const registeredUser = res.data.user ?? null;
    setUser(registeredUser);
    toast.success(`Welcome to WomLus, ${registeredUser?.name ?? 'there'}!`);
    return res.data;
  };

  const logout = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setUser(null);
    setNotifications([]);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data) => {
    const res = await authAPI.updateProfile(data);
    setUser(res.data.user);
    toast.success('Profile updated!');
    return res.data;
  };

  const markNotificationsRead = async () => {
    try {
      await authAPI.markNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      notifications,
      unreadCount,
      login,
      register,
      logout,
      updateProfile,
      fetchNotifications,
      markNotificationsRead,
      isAdmin: user?.role === 'admin',
      isAuthor: user?.role === 'author' || user?.role === 'admin',
      isLoggedIn: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
